import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse } from "@fortawesome/pro-regular-svg-icons";
import { faWandMagicSparkles, faArrowUpRightFromSquare, faPuzzle } from "@fortawesome/pro-solid-svg-icons";

import Card from "@/components/card/index.tsx";
import ButtonLockDependent from "@/components/buttons/button-lock-dependent";
import ValidationPack from "@/pages/takeoff/layouts/validation/models/validation-pack";
import ValidationState from "@/models/enums/validation-state";
import useProjectContext from "@/services/context/project/use-project-context.ts";
import ExternalLinkButton from "@/components/buttons/external-link-button";
import shellHighlight from "@/pages/takeoff/layouts/measures/layouts/overview/styles/shell-highlight.module.css";

interface Calcul1BimCardActionProps {
    hasSurfaces: boolean;
    validationPacks: ValidationPack[];
    isLoading: boolean;
    onClick: () => void;
}

const Calcul1BimCardAction: React.FC<Calcul1BimCardActionProps> = ({
    hasSurfaces,
    validationPacks,
    isLoading,
    onClick
}) => {

    const { project } = useProjectContext();

    const toValidate = validationPacks.filter(
        p => p.validation.state === ValidationState.ToValidate
    ).length;

    let content: React.ReactNode;

    if (toValidate === 0) {
        content = (
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                {hasSurfaces &&
                    <ExternalLinkButton
                        variant='simple'
                        size='xl'
                        href={`${import.meta.env.VITE_BOOMER_URL}/Management/Surfaces/${project.projectBoomerId}`}>
                        <div className='flex flex-row gap-x-2 items-center'>
                            <span>Accédez aux surfaces</span>
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className='text-xs' />
                        </div>
                    </ExternalLinkButton>
                }

                <ButtonLockDependent
                    variant='simple'
                    size='sm'
                    onClick={onClick}
                    disabled={isLoading}
                >
                    <div className='flex flex-row gap-x-2 items-center'>
                        <div className='flex flex-row gap-x-1 items-baseline'>
                            <FontAwesomeIcon icon={faHouse} />
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                        </div>

                        <span>{hasSurfaces ? 'Relancez le calcul' : 'Finalisez le calcul'}</span>
                    </div>
                </ButtonLockDependent>

                <div>
                    <span>Tous les étages ont été traités.</span>
                </div>
            </Card>
        );
    } else {
        content = (
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                <div className='flex flex-col gap-y-2'>
                    <span>Validez toutes les destinations de pièces pour obtenir :</span>

                    <ul className='list-disc list-inside pl-2 text-sm'>
                        <li>SHAB</li>
                        <li>SU</li>
                        <li>SDP</li>
                        <li>ST</li>
                    </ul>

                    <div className='flex flex-row items-center justify-between'>
                        <span>{validationPacks.length - toValidate}/{validationPacks.length} des étages ont été traités.</span>
                        <FontAwesomeIcon icon={faPuzzle}
                            className={`${shellHighlight.hint} rounded text-xl text-slate-900 cursor-help focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500`}
                            aria-label='Repérer les boutons des métrés coques'
                            data-tooltip-id='shell-icon-tooltip' />
                    </div>
                </div>
            </Card>
        );
    }

    return <>{content}</>;
};

export default Calcul1BimCardAction;